import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  CopyButton,
  Field,
  Modal,
  ModalActions,
  SearchSelect,
  Table,
  type Action,
} from '@evogard/ui';
import type {
  OpasuiteChannelTemplate,
  OpasuiteOverview,
  OpasuiteSendResult,
  OpasuiteTemplate,
} from '@evogard/contract';
import {
  normalizePhone,
  opasuiteBaseUrlProblem,
  templatePlainText,
} from '@evogard/domain';
import { api, ok, errorMessage } from '../../lib/api.js';
import { WhatsappPreview } from './WhatsappPreview.js';

/**
 * O resto do OpaSuite na tela de integrações (task 1465354197, 15/10/2026):
 * o endereço, os templates e onde cada um é usado. O token é a credencial da
 * integração, como as outras.
 *
 * Desde 16/10/2026 são três peças, uma por aba: o endereço vai para
 * "Conexão", ao lado do token; os templates e os pontos de envio têm aba
 * própria. As três leem a mesma consulta (`useOpasuiteOverview`), e a tela usa
 * a mesma para dizer na lista se falta o endereço.
 *
 * **Os templates são cadastrados uma vez e usados em vários pontos.** Quem
 * escolhe o template de cada ponto é a operação; quem preenche as variáveis é
 * o código daquele ponto. Por isso a aba dos pontos mostra o que cada variável
 * recebe, e só oferece templates com a mesma quantidade.
 *
 * Canais e templates do OpaSuite são lidos ao vivo, no cadastro. Se o OpaSuite
 * não responder, a tela diz a frase dele e não cadastra.
 */
export function opasuiteKey(tenantId: number | undefined) {
  return ['plataforma', 'integracoes', 'opasuite', tenantId];
}

export function useOpasuiteOverview(tenantId: number | undefined) {
  return useQuery({
    queryKey: opasuiteKey(tenantId),
    queryFn: async () => {
      const r = await api.opasuite.getOpasuite();
      if (r.status !== 200) {
        throw new Error(errorMessage(r.body, 'Não foi possível carregar o OpaSuite.'));
      }
      return r.body;
    },
    enabled: tenantId !== undefined,
  });
}

/** O aviso de cada aba: a lista nova no cache, e a frase do que aconteceu. */
function useStore(tenantId: number | undefined) {
  const client = useQueryClient();
  const [note, setNote] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  return {
    note,
    actionError,
    setNote,
    setActionError,
    store: (overview: OpasuiteOverview, message: string) => {
      client.setQueryData(opasuiteKey(tenantId), overview);
      setActionError(null);
      setNote(message);
    },
    fail: (message: string) => {
      setNote(null);
      setActionError(message);
    },
  };
}

function Notices({
  error,
  actionError,
  note,
}: {
  error: Error | null;
  actionError: string | null;
  note: string | null;
}) {
  return (
    <>
      {error && <Alert tone="erro">{error.message}</Alert>}
      {actionError && <Alert tone="erro">{actionError}</Alert>}
      {note && <Alert tone="sucesso">{note}</Alert>}
    </>
  );
}

/** O endereço do OpaSuite, na aba "Conexão". */
export function OpasuiteAddressPanel({
  tenantId,
  canEdit,
}: {
  tenantId: number | undefined;
  canEdit: boolean;
}) {
  const query = useOpasuiteOverview(tenantId);
  const { note, actionError, store } = useStore(tenantId);
  return (
    <div className="flex flex-col gap-3">
      <Notices error={query.error} actionError={actionError} note={note} />
      {query.data && (
        <AddressSection
          baseUrl={query.data.settings.baseUrl}
          canEdit={canEdit}
          onSaved={(overview) => store(overview, 'Endereço do OpaSuite salvo.')}
        />
      )}
    </div>
  );
}

/** Os templates cadastrados: cadastrar, enviar teste, editar e arquivar. */
export function OpasuiteTemplatesPanel({
  tenantId,
  canEdit,
}: {
  tenantId: number | undefined;
  canEdit: boolean;
}) {
  const query = useOpasuiteOverview(tenantId);
  const { note, actionError, setNote, setActionError, store } = useStore(tenantId);
  const [creating, setCreating] = useState(false);
  /** O template aberto no envio de teste — modal largo, com a prévia ao lado. */
  const [testing, setTesting] = useState<OpasuiteTemplate | null>(null);

  const archive = useMutation({
    mutationFn: async (id: number) => {
      const r = await api.opasuite.updateOpasuiteTemplate({
        params: { id },
        body: { archived: true },
      });
      return ok(r, 200, 'Não foi possível arquivar o template.');
    },
  });

  const data = query.data;
  const hasAddress = Boolean(data?.settings.baseUrl);

  return (
    <div className="flex flex-col gap-4">
      <Notices error={query.error} actionError={actionError} note={note} />
      {canEdit && (
        <div>
          <Button
            icon="mais"
            size="sm"
            disabled={!hasAddress}
            title={
              hasAddress
                ? undefined
                : 'Informe o endereço do OpaSuite antes, na aba Conexão.'
            }
            onClick={() => {
              setNote(null);
              setActionError(null);
              setCreating(true);
            }}
          >
            Cadastrar template
          </Button>
        </div>
      )}

      <section className="flex flex-col gap-3" aria-labelledby="opasuite-templates">
        <h3 id="opasuite-templates" className="text-sm font-semibold text-texto">
          Templates cadastrados
        </h3>
        <Table
          paginate={false}
          label="Templates do OpaSuite desta base"
          items={data?.templates ?? []}
          loading={query.isLoading}
          skeletonRows={2}
          rowKey={(t) => String(t.id)}
          rowTitle={(t) => t.name}
          rowSubtitle={(t) => t.channelName}
          rowIcon="telefone"
          empty={
            hasAddress
              ? 'Nenhum template cadastrado. O primeiro entra por Cadastrar template.'
              : 'Informe o endereço do OpaSuite, na aba Conexão, para cadastrar o primeiro template.'
          }
          rowActions={(t): Action[] =>
            !canEdit
              ? []
              : [
                  {
                    label: 'Enviar teste',
                    icon: 'seta-direita',
                    run: () => setTesting(t),
                  },
                  {
                    label: 'Editar',
                    icon: 'etiqueta',
                    form: (close) => (
                      <TemplateOptionsForm
                        template={t}
                        onSaved={(overview) => {
                          store(overview, `${t.name} foi salvo.`);
                          close();
                        }}
                        onCancel={close}
                      />
                    ),
                  },
                  {
                    label: 'Arquivar',
                    icon: 'bloqueado',
                    tone: 'perigo',
                    form: (close) => (
                      <div className="flex flex-col gap-4">
                        <p className="text-sm text-texto-suave">
                          <strong className="font-medium text-texto">{t.name}</strong> sai
                          da lista. O template continua existindo no OpaSuite.
                        </p>
                        {t.purposes.length > 0 && (
                          <Alert tone="alerta">
                            Ele ainda é usado em {t.purposes.length}{' '}
                            {t.purposes.length === 1 ? 'ponto' : 'pontos'}. Escolha outro
                            template para eles antes de arquivar.
                          </Alert>
                        )}
                        <ModalActions>
                          <Button type="button" variant="contorno" onClick={close}>
                            Cancelar
                          </Button>
                          <Button
                            type="button"
                            variant="perigo"
                            disabled={archive.isPending || t.purposes.length > 0}
                            onClick={() =>
                              archive.mutate(t.id, {
                                onSuccess: (overview) => {
                                  store(overview, `${t.name} foi arquivado.`);
                                  close();
                                },
                                onError: (e: Error) => {
                                  setNote(null);
                                  setActionError(e.message);
                                  close();
                                },
                              })
                            }
                          >
                            {archive.isPending ? 'Arquivando…' : 'Arquivar'}
                          </Button>
                        </ModalActions>
                      </div>
                    ),
                  },
                ]
          }
          columns={[
            {
              title: 'Template',
              alignment: 'inicio' as const,
              minWidth: '14rem',
              cell: (t) => (
                <div className="flex min-w-0 flex-col">
                  <p className="truncate font-semibold">{t.name}</p>
                  <p className="truncate text-xs text-texto-suave">
                    {t.shortcut ?? t.templateId} · {t.channelName}
                  </p>
                </div>
              ),
            },
            {
              title: 'Variáveis',
              minWidth: '12rem',
              // a contagem, e não as fichas: um template de PI tem 20 posições
              cell: (t) => (
                <span className="tabular-nums text-sm">
                  {t.variables.length}{' '}
                  {t.variables.length === 1 ? 'variável' : 'variáveis'}
                </span>
              ),
            },
            {
              title: 'Envio',
              minWidth: '10rem',
              cell: (t) => (
                <span className="inline-flex flex-wrap justify-center gap-1">
                  {t.allowStartedService && (
                    <Badge tone="neutro">Com atendimento aberto</Badge>
                  )}
                  {t.openCustomerService && <Badge tone="neutro">Abre atendimento</Badge>}
                  {t.fallbackMediaUrl && <Badge tone="neutro">Mídia própria</Badge>}
                  {!t.allowStartedService &&
                    !t.openCustomerService &&
                    !t.fallbackMediaUrl && (
                      <span className="text-xs text-texto-suave">Padrão do OpaSuite</span>
                    )}
                </span>
              ),
            },
            {
              title: 'Uso',
              minWidth: '6rem',
              cell: (t) =>
                t.purposes.length === 0 ? (
                  <span className="text-xs text-texto-suave">Sem uso</span>
                ) : (
                  <Badge tone="sucesso">
                    {t.purposes.length} {t.purposes.length === 1 ? 'ponto' : 'pontos'}
                  </Badge>
                ),
            },
          ]}
        />
      </section>

      <Modal
        open={testing !== null}
        onOpenChange={(open) => !open && setTesting(null)}
        title="Enviar teste"
        descricao={testing ? `${testing.name} · ${testing.channelName}` : undefined}
        size="xl"
      >
        {testing && <TestSendForm template={testing} onCancel={() => setTesting(null)} />}
      </Modal>

      <Modal
        open={creating}
        onOpenChange={setCreating}
        title="Cadastrar template do OpaSuite"
        descricao="Escolha o canal e o template. O texto e as variáveis vêm do OpaSuite."
        size="lg"
      >
        {creating && (
          <CreateTemplateForm
            onSaved={(overview, name) => {
              store(overview, `${name} foi cadastrado.`);
              setCreating(false);
            }}
            onCancel={() => setCreating(false)}
          />
        )}
      </Modal>
    </div>
  );
}

/** Qual template cada ponto do sistema dispara. */
export function OpasuitePurposesPanel({
  tenantId,
  canEdit,
}: {
  tenantId: number | undefined;
  canEdit: boolean;
}) {
  const query = useOpasuiteOverview(tenantId);
  const { note, actionError, store, fail } = useStore(tenantId);
  return (
    <div className="flex flex-col gap-4">
      <Notices error={query.error} actionError={actionError} note={note} />
      {query.data && (
        <PurposesSection
          overview={query.data}
          canEdit={canEdit}
          onSaved={store}
          onError={fail}
        />
      )}
    </div>
  );
}

/** O endereço do OpaSuite desta base: mostra, e troca. */
function AddressSection({
  baseUrl,
  canEdit,
  onSaved,
}: {
  baseUrl: string | null;
  canEdit: boolean;
  onSaved: (overview: OpasuiteOverview) => void;
}) {
  const [editing, setEditing] = useState(baseUrl === null);
  const [value, setValue] = useState(baseUrl ?? '');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const problem = opasuiteBaseUrlProblem(value);

  const save = useMutation({
    mutationFn: async () => {
      const r = await api.opasuite.saveOpasuiteSettings({ body: { baseUrl: value } });
      return ok(r, 200, 'Não foi possível salvar o endereço.');
    },
    onSuccess: (overview) => {
      setEditing(false);
      setSubmitted(false);
      setError(null);
      setValue(overview.settings.baseUrl ?? '');
      onSaved(overview);
    },
    onError: (e: Error) => setError(e.message),
  });

  return (
    <section className="flex flex-col gap-3" aria-labelledby="opasuite-endereco">
      <h3 id="opasuite-endereco" className="text-sm font-semibold text-texto">
        Endereço do OpaSuite
      </h3>
      {!editing || !canEdit ? (
        <div className="flex flex-wrap items-center gap-3">
          {baseUrl ? (
            <code className="font-mono text-sm text-texto">{baseUrl}</code>
          ) : (
            <Badge tone="alerta">Sem endereço</Badge>
          )}
          {canEdit && (
            <Button variant="contorno" size="sm" onClick={() => setEditing(true)}>
              Trocar endereço
            </Button>
          )}
        </div>
      ) : (
        <form
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            setSubmitted(true);
            if (!problem) save.mutate();
          }}
        >
          <div className="min-w-0 flex-1">
            <Field
              label="Endereço"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              autoComplete="off"
              spellCheck={false}
              error={submitted ? (problem ?? error) : error}
              help="O domínio do OpaSuite da casa, como https://evogard.opasuite.com.br. O /api/v1 a casa acrescenta."
            />
          </div>
          <div className="flex gap-2">
            {baseUrl && (
              <Button
                type="button"
                variant="contorno"
                onClick={() => {
                  setEditing(false);
                  setValue(baseUrl);
                  setError(null);
                }}
              >
                Cancelar
              </Button>
            )}
            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? 'Salvando…' : 'Salvar endereço'}
            </Button>
          </div>
        </form>
      )}
    </section>
  );
}

/** Onde cada template é usado: um select por ponto declarado no código. */
function PurposesSection({
  overview,
  canEdit,
  onSaved,
  onError,
}: {
  overview: OpasuiteOverview;
  canEdit: boolean;
  onSaved: (overview: OpasuiteOverview, message: string) => void;
  onError: (message: string) => void;
}) {
  const link = useMutation({
    mutationFn: async (input: { purpose: string; templateId: number | null }) => {
      const r = await api.opasuite.setOpasuitePurposeTemplate({
        params: { purpose: input.purpose },
        body: { templateId: input.templateId },
      });
      return ok(r, 200, 'Não foi possível salvar o ponto de uso.');
    },
  });

  return (
    <section className="flex flex-col gap-3" aria-labelledby="opasuite-pontos">
      <h3 id="opasuite-pontos" className="text-sm font-semibold text-texto">
        Onde cada template é usado
      </h3>
      {overview.purposes.length === 0 ? (
        <p className="text-sm text-texto-suave">
          Nenhum ponto do sistema dispara WhatsApp ainda. O primeiro será o envio de PI do
          Periférico, assim que o que cada variável recebe estiver definido. Enquanto
          isso, o template pode ser cadastrado e conferido por Enviar teste.
        </p>
      ) : (
        <ul className="flex flex-col divide-y divide-borda-suave">
          {overview.purposes.map((p) => {
            const compatible = overview.templates.filter(
              (t) => t.variables.length === p.variables.length,
            );
            return (
              <li
                key={p.purpose}
                className="flex flex-col gap-3 py-3 first:pt-0 sm:flex-row sm:items-start sm:justify-between"
              >
                <div className="flex min-w-0 flex-col gap-1.5">
                  <p className="font-semibold text-texto">{p.label}</p>
                  <Variables names={p.variables} />
                </div>
                <div className="flex flex-col gap-1.5 sm:w-72">
                  <SearchSelect
                    label={`Template de ${p.label}`}
                    hiddenLabel
                    sortable
                    clearable
                    placeholder="Sem template — não dispara"
                    searchPlaceholder="Buscar template…"
                    emptyMessage="Nenhum template com este nome."
                    options={compatible.map((t) => ({
                      value: String(t.id),
                      label: t.name,
                    }))}
                    value={p.templateId === null ? '' : String(p.templateId)}
                    disabled={!canEdit || link.isPending}
                    onChange={(value) => {
                      const templateId = value === '' ? null : Number(value);
                      link.mutate(
                        { purpose: p.purpose, templateId },
                        {
                          onSuccess: (next) =>
                            onSaved(
                              next,
                              templateId === null
                                ? `${p.label} deixou de disparar WhatsApp.`
                                : `${p.label} passou a usar o template escolhido.`,
                            ),
                          onError: (e: Error) => onError(e.message),
                        },
                      );
                    }}
                    help={
                      compatible.length === 0
                        ? `Nenhum template cadastrado tem ${p.variables.length} variável(is).`
                        : undefined
                    }
                  />
                  {p.templateId === null ? (
                    <Badge tone="alerta">Sem template</Badge>
                  ) : (
                    <Badge tone="sucesso">Configurado</Badge>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

/**
 * As variáveis como fichas NUMERADAS. O número é o que manda: o OpaSuite
 * preenche por posição, e o mesmo nome pode aparecer em todas elas
 * (`{{variavel_vazia}}`, medido em 15/10/2026).
 */
function Variables({ names }: { names: string[] }) {
  if (names.length === 0) {
    return <span className="text-xs text-texto-suave">Sem variáveis</span>;
  }
  return (
    <span className="inline-flex flex-wrap gap-1">
      {names.map((name, i) => (
        <code
          key={`${i}-${name}`}
          className="rounded border border-borda-suave bg-superficie-suave px-1.5 py-0.5 font-mono text-xs text-texto-suave"
        >
          {`${i + 1} · ${name}`}
        </code>
      ))}
    </span>
  );
}

/** Cadastro: canal → template (lidos do OpaSuite) → nome e opções do envio. */
function CreateTemplateForm({
  onSaved,
  onCancel,
}: {
  onSaved: (overview: OpasuiteOverview, name: string) => void;
  onCancel: () => void;
}) {
  const [channelId, setChannelId] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [name, setName] = useState('');
  const [media, setMedia] = useState('');
  const [allowStarted, setAllowStarted] = useState(false);
  const [openService, setOpenService] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const channels = useQuery({
    queryKey: ['plataforma', 'integracoes', 'opasuite', 'canais'],
    queryFn: async () =>
      ok(
        await api.opasuite.listOpasuiteChannels(),
        200,
        'Não foi possível ler os canais.',
      ),
    staleTime: 0,
  });

  const templates = useQuery({
    queryKey: ['plataforma', 'integracoes', 'opasuite', 'canais', channelId, 'templates'],
    queryFn: async () =>
      ok(
        await api.opasuite.listOpasuiteChannelTemplates({ params: { channelId } }),
        200,
        'Não foi possível ler os templates do canal.',
      ),
    enabled: channelId !== '',
    staleTime: 0,
  });

  /*
   * O template de "detalhes de pagamento" sai da lista: as variáveis dele são
   * um objeto, e a casa envia lista. A ajuda do campo diz quantos ficaram de
   * fora — sumir sem aviso faria a pessoa procurar no OpaSuite um template que
   * está lá.
   */
  const sendable = (templates.data?.items ?? []).filter((t) => t.supported);
  const hidden = (templates.data?.items.length ?? 0) - sendable.length;
  const templateHelp =
    templates.data?.available && templates.data.items.length === 0
      ? 'Este canal não tem template no OpaSuite.'
      : hidden > 0
        ? `${hidden} ${hidden === 1 ? 'template de detalhes de pagamento ficou' : 'templates de detalhes de pagamento ficaram'} de fora: a casa ainda não envia esse tipo.`
        : undefined;

  const chosen: OpasuiteChannelTemplate | undefined = templates.data?.items.find(
    (t) => t.id === templateId,
  );

  const problems = {
    channel: channelId === '' ? 'Escolha o canal.' : null,
    template: templateId === '' ? 'Escolha o template.' : null,
    name: name.trim().length === 0 ? 'Informe o nome do template.' : null,
    media:
      media.trim().length === 0 || /^https:\/\/\S+$/.test(media.trim())
        ? null
        : 'A mídia alternativa precisa ser um link https.',
  };
  const hasProblem = Object.values(problems).some((p) => p !== null);
  const shown = (p: string | null) => (submitted ? p : null);

  const save = useMutation({
    mutationFn: async () => {
      const r = await api.opasuite.createOpasuiteTemplate({
        body: {
          name: name.trim(),
          channelId,
          templateId,
          fallbackMediaUrl: media.trim() || null,
          allowStartedService: allowStarted,
          openCustomerService: openService,
        },
      });
      return ok(r, 201, 'Não foi possível cadastrar o template.');
    },
    onSuccess: (overview) => onSaved(overview, name.trim()),
    onError: (e: Error) => setError(e.message),
  });

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        setSubmitted(true);
        setError(null);
        if (!hasProblem) save.mutate();
      }}
    >
      {error && <Alert tone="erro">{error}</Alert>}
      {channels.data && !channels.data.available && (
        <Alert tone="erro" title="O OpaSuite não respondeu.">
          {channels.data.detail}
        </Alert>
      )}
      {channels.isError && <Alert tone="erro">{channels.error.message}</Alert>}

      <SearchSelect
        label="Canal"
        sortable
        loading={channels.isLoading}
        placeholder={channels.isLoading ? 'Lendo os canais…' : 'Escolha o canal'}
        searchPlaceholder="Buscar canal…"
        emptyMessage="Nenhum canal com este nome."
        options={(channels.data?.items ?? []).map((ch) => ({
          value: ch.id,
          label: `${ch.name}${ch.status && ch.status !== 'A' ? ' (inativo)' : ''}`,
        }))}
        value={channelId}
        error={shown(problems.channel)}
        help={
          channels.data?.available && channels.data.items.length === 0
            ? 'A conta do OpaSuite não tem canal de WhatsApp.'
            : undefined
        }
        onChange={(value) => {
          setChannelId(value);
          setTemplateId('');
        }}
      />

      {channelId !== '' && (
        <>
          {templates.data && !templates.data.available && (
            <Alert tone="erro">{templates.data.detail}</Alert>
          )}
          <SearchSelect
            label="Template"
            sortable
            loading={templates.isLoading}
            placeholder={
              templates.isLoading ? 'Lendo os templates…' : 'Escolha o template'
            }
            searchPlaceholder="Buscar pelo atalho…"
            emptyMessage="Nenhum template com este atalho."
            options={sendable.map((t) => ({
              value: t.id,
              label: `${t.shortcut ?? t.id} · ${t.variables.length} ${
                t.variables.length === 1 ? 'variável' : 'variáveis'
              }`,
            }))}
            value={templateId}
            error={shown(problems.template)}
            help={templateHelp}
            onChange={(value) => {
              setTemplateId(value);
              const picked = sendable.find((t) => t.id === value);
              if (picked && name.trim() === '') setName(picked.shortcut ?? '');
            }}
          />
        </>
      )}

      {chosen && (
        <div className="flex flex-col gap-2 rounded-lg border border-borda-suave bg-superficie-suave p-3">
          <p className="max-h-60 overflow-y-auto whitespace-pre-wrap text-sm text-texto">
            {templatePlainText(chosen.text) || 'Sem texto.'}
          </p>
          <Variables names={chosen.variables} />
        </div>
      )}

      <Field
        label="Nome na casa"
        value={name}
        onChange={(e) => setName(e.target.value)}
        error={shown(problems.name)}
        help="Como a equipe vai reconhecer este template. Ex.: Envio de PI."
      />
      <Field
        label="Mídia alternativa (opcional)"
        value={media}
        onChange={(e) => setMedia(e.target.value)}
        autoComplete="off"
        spellCheck={false}
        error={shown(problems.media)}
        help="Troca a imagem ou o arquivo do template no envio. Em branco usa o do OpaSuite."
      />
      <OptionChecks
        allowStarted={allowStarted}
        openService={openService}
        onAllowStarted={setAllowStarted}
        onOpenService={setOpenService}
      />

      <ModalActions>
        <Button type="button" variant="contorno" onClick={onCancel}>
          Cancelar
        </Button>
        <Button type="submit" disabled={save.isPending}>
          {save.isPending ? 'Conferindo no OpaSuite…' : 'Cadastrar'}
        </Button>
      </ModalActions>
    </form>
  );
}

/** As duas opções do envio, com o que cada uma faz no OpaSuite. */
function OptionChecks({
  allowStarted,
  openService,
  onAllowStarted,
  onOpenService,
}: {
  allowStarted: boolean;
  openService: boolean;
  onAllowStarted: (value: boolean) => void;
  onOpenService: (value: boolean) => void;
}) {
  return (
    <fieldset className="flex flex-col gap-2">
      <legend className="mb-1 text-sm font-semibold text-texto">Opções do envio</legend>
      <label className="flex items-start gap-2 text-sm">
        <input
          type="checkbox"
          checked={allowStarted}
          onChange={(e) => onAllowStarted(e.target.checked)}
          className="mt-0.5 size-4 rounded border-borda-forte accent-primaria-texto"
        />
        <span>
          Enviar mesmo com atendimento em andamento
          <span className="block text-xs text-texto-suave">
            Sem isto, o OpaSuite não envia para quem já está sendo atendido.
          </span>
        </span>
      </label>
      <label className="flex items-start gap-2 text-sm">
        <input
          type="checkbox"
          checked={openService}
          onChange={(e) => onOpenService(e.target.checked)}
          className="mt-0.5 size-4 rounded border-borda-forte accent-primaria-texto"
        />
        <span>
          Abrir um atendimento no envio
          <span className="block text-xs text-texto-suave">
            Se já houver um em andamento, nenhum novo é aberto.
          </span>
        </span>
      </label>
    </fieldset>
  );
}

/** Editar: nome e opções do envio. Canal e template não se trocam — cadastra-se outro. */
function TemplateOptionsForm({
  template,
  onSaved,
  onCancel,
}: {
  template: OpasuiteTemplate;
  onSaved: (overview: OpasuiteOverview) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(template.name);
  const [media, setMedia] = useState(template.fallbackMediaUrl ?? '');
  const [allowStarted, setAllowStarted] = useState(template.allowStartedService);
  const [openService, setOpenService] = useState(template.openCustomerService);
  const [error, setError] = useState<string | null>(null);

  const mediaProblem =
    media.trim().length === 0 || /^https:\/\/\S+$/.test(media.trim())
      ? null
      : 'A mídia alternativa precisa ser um link https.';

  const save = useMutation({
    mutationFn: async () => {
      const r = await api.opasuite.updateOpasuiteTemplate({
        params: { id: template.id },
        body: {
          name: name.trim(),
          fallbackMediaUrl: media.trim() || null,
          allowStartedService: allowStarted,
          openCustomerService: openService,
        },
      });
      return ok(r, 200, 'Não foi possível salvar o template.');
    },
    onSuccess: onSaved,
    onError: (e: Error) => setError(e.message),
  });

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (name.trim() && !mediaProblem) save.mutate();
      }}
    >
      {error && <Alert tone="erro">{error}</Alert>}
      <p className="text-sm text-texto-suave">
        Canal e template não mudam aqui. Para trocar, cadastre o outro template e
        escolha-o nos pontos de uso.
      </p>
      <Field
        label="Nome na casa"
        value={name}
        onChange={(e) => setName(e.target.value)}
        error={name.trim() ? null : 'Informe o nome do template.'}
      />
      <Field
        label="Mídia alternativa (opcional)"
        value={media}
        onChange={(e) => setMedia(e.target.value)}
        autoComplete="off"
        spellCheck={false}
        error={mediaProblem}
      />
      <OptionChecks
        allowStarted={allowStarted}
        openService={openService}
        onAllowStarted={setAllowStarted}
        onOpenService={setOpenService}
      />
      <ModalActions>
        <Button type="button" variant="contorno" onClick={onCancel}>
          Cancelar
        </Button>
        <Button type="submit" disabled={save.isPending}>
          {save.isPending ? 'Salvando…' : 'Salvar'}
        </Button>
      </ModalActions>
    </form>
  );
}

/** O que a tela diz sobre cada desfecho do envio de teste. */
// @valor — as chaves são as situações do envio, valor gravado
const RESULT_TEXT: Record<OpasuiteSendResult['situacao'], string> = {
  enviada: 'O OpaSuite aceitou o envio.',
  repetida: 'Este envio já tinha saído.',
  falhou: 'O OpaSuite não enviou.',
  contato_bloqueado: 'Não enviado: o número está bloqueado na casa.',
  telefone_invalido: 'Não enviado: o número não é um celular com DDD.',
  template_nao_configurado: 'Não enviado: o template não existe mais nesta base.',
  template_incompativel: 'Não enviado: a quantidade de variáveis não bate.',
  integracao_nao_configurada: 'Não enviado: falta configurar o OpaSuite nesta base.',
};

/** O envio de teste: um número e uma caixa por variável, na ordem do template. */
function TestSendForm({
  template,
  onCancel,
}: {
  template: OpasuiteTemplate;
  onCancel: () => void;
}) {
  const [phone, setPhone] = useState('');
  const [values, setValues] = useState<string[]>(() => template.variables.map(() => ''));
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState<OpasuiteSendResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const phoneProblem =
    normalizePhone(phone) === null ? 'Informe um celular com DDD.' : null;
  const missing = values.some((v) => v.trim().length === 0);

  const send = useMutation({
    mutationFn: async () => {
      const r = await api.opasuite.sendOpasuiteTest({
        params: { id: template.id },
        body: { phone, variables: values.map((v) => v.trim()) },
      });
      return ok(r, 200, 'Não foi possível enviar o teste.');
    },
    onSuccess: (body) => {
      setError(null);
      setResult(body);
    },
    onError: (e: Error) => {
      setResult(null);
      setError(e.message);
    },
  });

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        setSubmitted(true);
        setResult(null);
        if (!phoneProblem && !missing) send.mutate();
      }}
    >
      {/*
       * Lado a lado: os campos rolam sozinhos à esquerda, e a prévia fica
       * parada à direita — um template de PI tem 20 posições, e a mensagem
       * precisa continuar à vista enquanto se preenche a 17ª.
       */}
      <div className="grid gap-6 lg:h-[min(36rem,65vh)] lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <div className="flex min-h-0 flex-col gap-4 lg:overflow-y-auto lg:px-1 lg:pr-3">
          <p className="text-sm text-texto-suave">
            Manda <strong className="font-medium text-texto">{template.name}</strong>{' '}
            agora, de verdade, para o número abaixo. O envio fica registrado como teste.
          </p>
          {error && <Alert tone="erro">{error}</Alert>}
          {result && (
            <Alert tone={result.situacao === 'enviada' ? 'sucesso' : 'erro'}>
              {RESULT_TEXT[result.situacao]}
              {result.detail ? ` ${result.detail}` : ''}
            </Alert>
          )}
          {result?.providerResponse && (
            <VendorResponseBlock response={result.providerResponse} />
          )}
          <Field
            label="Celular"
            mask="phone"
            inputMode="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            error={submitted ? phoneProblem : null}
          />
          {template.variables.map((name, i) => (
            <Field
              key={`${i}-${name}`}
              label={`Variável ${i + 1} · ${name}`}
              value={values[i] ?? ''}
              onChange={(e) =>
                setValues((current) =>
                  current.map((v, j) => (j === i ? e.target.value : v)),
                )
              }
              error={
                submitted && !(values[i] ?? '').trim() ? 'Preencha a variável.' : null
              }
            />
          ))}
        </div>
        <WhatsappPreview
          text={template.text}
          values={values}
          channel={template.channelName}
        />
      </div>
      <ModalActions>
        <Button type="button" variant="contorno" onClick={onCancel}>
          Fechar
        </Button>
        <Button type="submit" icon="seta-direita" disabled={send.isPending}>
          {send.isPending ? 'Enviando…' : 'Enviar teste'}
        </Button>
      </ModalActions>
    </form>
  );
}

/**
 * O que o OpaSuite respondeu, cru — o status HTTP e o corpo, com o JSON
 * indentado. É para quem configura: a frase do alerta acima é o que a casa
 * entendeu, e isto é o que ele disse. Corpo que não é JSON aparece como veio.
 */
function VendorResponseBlock({
  response,
}: {
  response: { status: number; body: string };
}) {
  let pretty = response.body;
  try {
    pretty = JSON.stringify(JSON.parse(response.body), null, 2);
  } catch {
    // não é JSON: fica como veio
  }
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-semibold text-texto">
          Resposta do OpaSuite{' '}
          <span className="font-normal text-texto-suave">· HTTP {response.status}</span>
        </p>
        <CopyButton text={pretty} label="Copiar resposta" />
      </div>
      <pre className="max-h-60 overflow-auto whitespace-pre-wrap break-all rounded-lg border border-borda-suave bg-superficie-suave p-3 font-mono text-xs text-texto">
        {pretty || '(corpo vazio)'}
      </pre>
    </div>
  );
}
